import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { BundleSplitColorSwatch } from "@/components/product/BundleSplitColorSwatch";

interface ProductVariant {
  id: string;
  pot_type: string;
  size: string;
  pot_color: string;
  price: number;
  discount_price?: number;
  stock: number;
  image_url?: string;
}

interface TypeColorRule {
  pot_type: string;
  allowed_colors: string[];
}

interface DependencyRule {
  pot_size: string;
  allowed_pot_types: string[];
  allowed_pot_colors: string[];
  type_color_rules?: TypeColorRule[];
}

type AttributeValue = { id: string; name: string; enabled: boolean; metadata?: any; position?: number };
 type Attribute = { type: 'pot_size' | 'pot_type' | 'pot_color'; label: string; values: AttributeValue[] };
 
 interface EnhancedProductVariantSelectorProps {
   variants: ProductVariant[];
   selectedVariant: ProductVariant | null;
   onVariantChange: (variant: ProductVariant) => void;
   attributes?: Attribute[];
   dependencyRules?: DependencyRule[];
   isBundle?: boolean;
 }

export const EnhancedProductVariantSelector = ({
  variants,
  selectedVariant,
  onVariantChange,
  attributes,
  dependencyRules = [],
  isBundle = false,
}: EnhancedProductVariantSelectorProps) => {
  if (variants.length === 0) return null;

  // Ensure dependencyRules is always an array (handle {} from database)
  const rules = Array.isArray(dependencyRules) ? dependencyRules : [];

  // Respect admin-defined attributes (labels and enabled values) when provided
  const attrMap = new Map(attributes?.map((a) => [a.type, a]) || []);
  const rawAllowedPotTypes = attributes ? (attrMap.get('pot_type')?.values?.filter(v => v.enabled).map(v => v.name) || []) : undefined;
  const rawAllowedSizes = attributes ? (attrMap.get('pot_size')?.values?.filter(v => v.enabled).map(v => v.name) || []) : undefined;
  const rawAllowedColors = attributes ? (attrMap.get('pot_color')?.values?.filter(v => v.enabled).map(v => v.name) || []) : undefined;
  // If admin lists are empty, treat as undefined to avoid filtering everything out
  const allowedPotTypes = rawAllowedPotTypes && rawAllowedPotTypes.length > 0 ? rawAllowedPotTypes : undefined;
  const allowedSizes = rawAllowedSizes && rawAllowedSizes.length > 0 ? rawAllowedSizes : undefined;
  const allowedColors = rawAllowedColors && rawAllowedColors.length > 0 ? rawAllowedColors : undefined;
  const allowedPotTypesL = allowedPotTypes?.map((n) => String(n).toLowerCase());
  const allowedSizesL = allowedSizes?.map((n) => String(n).toLowerCase());
  const allowedColorsL = allowedColors?.map((n) => String(n).toLowerCase());

  // Filter variants by allowed attributes if provided (case-insensitive)
  const baseVariants = attributes
    ? variants.filter(v =>
        (!allowedPotTypesL || allowedPotTypesL.includes(String(v.pot_type).toLowerCase())) &&
        (!allowedSizesL || allowedSizesL.includes(String(v.size).toLowerCase())) &&
        (!allowedColorsL || allowedColorsL.includes(String(v.pot_color).toLowerCase()))
      )
    : variants;

  // Option lists - from admin-defined values when available; fall back to variants if empty
  const potTypes = attributes 
    ? (allowedPotTypes ?? [...new Set(baseVariants.map((v) => v.pot_type))]) 
    : [...new Set(baseVariants.map((v) => v.pot_type))];
  const sizes = attributes 
    ? (allowedSizes ?? [...new Set(baseVariants.map((v) => v.size))]) 
    : [...new Set(baseVariants.map((v) => v.size))];
  const colors = attributes 
    ? (allowedColors ?? [...new Set(baseVariants.map((v) => v.pot_color))]) 
    : [...new Set(baseVariants.map((v) => v.pot_color))];

  // Build color -> hex map from admin metadata
  const colorHexMap = new Map<string, string>();
  if (attributes) {
    const colorAttr = attrMap.get('pot_color');
    colorAttr?.values?.forEach((v) => {
      const hex = (v as any)?.metadata?.hexColor || (v as any)?.metadata?.hex || '';
      if (hex) colorHexMap.set(String(v.name).toLowerCase(), hex);
    });
  }

  // For bundles: build a map of pot_color -> child hex colors from attribute metadata
  const bundleChildHexMap = new Map<string, string[]>();
  if (isBundle && attributes) {
    const colorAttr = attrMap.get('pot_color');
    colorAttr?.values?.forEach((v) => {
      const meta = v.metadata as any;
      if (meta?.isComposite && Array.isArray(meta?.childColors)) {
        const childHexes = meta.childColors
          .map((child: any) => {
            const childColor = (child.color || child.pot_color)?.toLowerCase();
            if (!childColor) return null;
            return colorHexMap.get(childColor) || getColorHex(childColor);
          })
          .filter((h: string | null): h is string => h !== null);
        if (childHexes.length > 1) {
          bundleChildHexMap.set(v.name, childHexes);
        }
      }
    });
  }
  const potLabel = attrMap.get('pot_type')?.label || 'Pot';
  const colorLabel = attrMap.get('pot_color')?.label || 'Color';
  const sizeLabel = attrMap.get('pot_size')?.label || 'Size';

  // Availability helpers based on current selection
  // IMPORTANT: These functions return options based on variant EXISTENCE, not stock levels
  // This ensures customers can see and select all variant combinations, even if out of stock
  const availableSizes = (potType?: string, color?: string) => {
    return [
      ...new Set(
        baseVariants
          .filter(
            (v) => 
              (!potType || potType === "" || v.pot_type === potType) && 
              (!color || color === "" || v.pot_color === color)
          )
          .map((v) => v.size)
      ),
    ];
  };

  const availableColors = (potType?: string, size?: string) => {
    // Apply dependency rules if a size is selected
    const rule = size ? rules.find(r => r.pot_size.toLowerCase() === size.toLowerCase()) : null;
    
    const colorsFromVariants = [
      ...new Set(
        baseVariants
          .filter(
            (v) => 
              (!potType || potType === "" || v.pot_type === potType) && 
              (!size || size === "" || v.size === size)
          )
          .map((v) => v.pot_color)
      ),
    ];
    
    if (!rule) return colorsFromVariants;
    
    // First apply size-level color restrictions
    let filteredColors = colorsFromVariants;
    if (rule.allowed_pot_colors && rule.allowed_pot_colors.length > 0) {
      filteredColors = colorsFromVariants.filter(color => 
        rule.allowed_pot_colors.some(allowed => allowed.toLowerCase() === color.toLowerCase())
      );
    }
    
    // Then apply type-specific color restrictions (more granular)
    if (potType && rule.type_color_rules && rule.type_color_rules.length > 0) {
      const typeRule = rule.type_color_rules.find(tcr => 
        tcr.pot_type.toLowerCase() === potType.toLowerCase()
      );
      if (typeRule && typeRule.allowed_colors.length > 0) {
        filteredColors = filteredColors.filter(color =>
          typeRule.allowed_colors.some(allowed => allowed.toLowerCase() === color.toLowerCase())
        );
      }
    }
    
    return filteredColors;
  };

  // Pot types depend ONLY on size (not color) - this is the correct flow
  const availablePotTypes = (size?: string) => {
    // Apply dependency rules if a size is selected
    const rule = size ? rules.find(r => r.pot_size.toLowerCase() === size.toLowerCase()) : null;
    const allowedByRule = rule?.allowed_pot_types;
    
    const typesFromVariants = [
      ...new Set(
        baseVariants
          .filter((v) => !size || size === "" || v.size === size)
          .map((v) => v.pot_type)
      ),
    ];
    
    // If dependency rule exists for this size, filter by allowed types
    if (allowedByRule && allowedByRule.length > 0) {
      return typesFromVariants.filter(type => 
        allowedByRule.some(allowed => allowed.toLowerCase() === type.toLowerCase())
      );
    }
    
    return typesFromVariants;
  };
  const findClosestVariant = (next: {
    pot_type?: string;
    size?: string;
    pot_color?: string;
  }): ProductVariant | undefined => {
    const { pot_type, size, pot_color } = next;

    // Exact match
    let found = variants.find(
      (v) =>
        (pot_type ? v.pot_type === pot_type : true) &&
        (size ? v.size === size : true) &&
        (pot_color ? v.pot_color === pot_color : true)
    );
    if (found) return found;

    // Prefer by pot_type + size
    if (pot_type && size) {
      found = variants.find((v) => v.pot_type === pot_type && v.size === size);
      if (found) return found;
    }

    // Prefer by pot_type only
    if (pot_type) {
      found = variants.find((v) => v.pot_type === pot_type);
      if (found) return found;
    }

    // Prefer by size only
    if (size) {
      found = variants.find((v) => v.size === size);
      if (found) return found;
    }

    // Prefer by color only
    if (pot_color) {
      found = variants.find((v) => v.pot_color === pot_color);
      if (found) return found;
    }

    return variants[0];
  };

  const handleSelect = (attr: "pot_type" | "size" | "pot_color", value: string) => {
    let next = {
      pot_type: attr === "pot_type" ? value : selectedVariant?.pot_type,
      size: attr === "size" ? value : selectedVariant?.size,
      pot_color: attr === "pot_color" ? value : selectedVariant?.pot_color,
    };

    // Flow: Size → Type + Color, Type → Color only, Color → nothing
    if (attr === "size") {
      // Size change: reset pot_type and color if they're not available for new size
      const availTypes = availablePotTypes(value);
      
      // Reset pot_type if current one is not available for new size
      if (next.pot_type && !availTypes.includes(next.pot_type)) {
        next.pot_type = availTypes.length > 0 ? availTypes[0] : undefined;
      }
      
      // Now get colors based on the (possibly updated) pot_type
      const availColors = availableColors(next.pot_type, value);
      
      // Reset pot_color if current one is not available
      if (next.pot_color && !availColors.includes(next.pot_color)) {
        next.pot_color = availColors.length > 0 ? availColors[0] : undefined;
      }
    } else if (attr === "pot_type") {
      // Pot type change: only reset color if not available for new type
      const availColors = availableColors(value, next.size);
      
      if (next.pot_color && !availColors.includes(next.pot_color)) {
        next.pot_color = availColors.length > 0 ? availColors[0] : undefined;
      }
    }
    // Color change: no cascading effects

    const match = findClosestVariant(next);
    if (match) onVariantChange(match);
  };

  const currentPotType = selectedVariant?.pot_type || "";
  const currentSize = selectedVariant?.size || "";
  const currentColor = selectedVariant?.pot_color || "";

  // Apply dependency rules: Size → Type → Color (hierarchical filtering)
  const sizesAvail = sizes; // All sizes always available
  const potTypesAvail = availablePotTypes(currentSize); // Types depend on size only
  const colorsAvail = availableColors(currentPotType, currentSize); // Colors depend on both size and type

  return (
    <div className="space-y-4 md:space-y-6">
        {/* Size */}
        <div className="space-y-2">
          <Label className="text-sm md:text-base font-medium">{sizeLabel}</Label>
          <div className="flex flex-wrap gap-2">
            {sizes.map((size) => {
              const selected = currentSize === size;
              return (
                <Button
                  key={size}
                  type="button"
                  size="sm"
                  variant={selected ? "secondary" : "outline"}
                  className={`rounded-full px-3 md:px-4 text-xs md:text-sm h-8 md:h-9`}
                  onClick={() => handleSelect("size", size)}
                >
                  <span className="capitalize">{size}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Pot Type */}
        <div className="space-y-2">
          <Label className="text-sm md:text-base font-medium">{potLabel}</Label>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {potTypesAvail.map((type) => {
              const selected = currentPotType === type;
              return (
                <Button
                  key={type}
                  type="button"
                  variant="outline"
                  className={`h-auto py-1.5 md:py-2 px-2 md:px-3 justify-center gap-1.5 border-2 whitespace-normal ${
                    selected ? "border-primary ring-2 ring-primary/30" : "border-input"
                  }`}
                  onClick={() => handleSelect("pot_type", type)}
                >
                  <span className="capitalize text-xs leading-tight text-center break-words">{type}</span>
                  {selected && <Check className="h-3 w-3 flex-shrink-0 text-primary" />}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Color */}
        <div className="space-y-2">
          <Label className="text-sm md:text-base font-medium">
            {colorLabel}: <span className="capitalize text-primary">{currentColor}</span>
          </Label>
          <div className="flex flex-wrap gap-2 md:gap-3">
            {colorsAvail.map((color) => {
              const selected = currentColor === color;
              const childHexes = bundleChildHexMap.get(color);
              return (
                <button
                  key={color}
                  type="button"
                  aria-label={`Select color ${color}`}
                  className={`relative w-9 h-9 md:w-10 md:h-10 rounded-full border-2 transition-shadow ${
                    selected ? "border-primary ring-2 ring-primary/30" : "border-input"
                  } hover:shadow`}
                  style={isBundle && childHexes ? undefined : { backgroundColor: colorHexMap.get(String(color).toLowerCase()) || (color as string) || getColorHex(color) }}
                  onClick={() => handleSelect("pot_color", color)}
                >
                  {isBundle && childHexes ? (
                    <BundleSplitColorSwatch colors={childHexes} className="w-full h-full" />
                  ) : (
                    <span className="sr-only">{color}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
    </div>
  );
};

// Helper function to get color hex values (product-driven, not theme)
const getColorHex = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    white: "#FFFFFF",
    black: "#000000",
    brown: "#8B4513",
    terracotta: "#E2725B",
    grey: "#808080",
    beige: "#E3DACE",
    charcoal: "#595959",
    stone: "#F1F1F1",
    mustard: "#FFDB58",
    "light-grey": "#D3D3D3",
    "forest-green": "#228B22",
    navy: "#000080",
    yellow: "#FFD700",
    orange: "#FF8C00",
    red: "#DC2626",
  };
  return colorMap[colorName?.toLowerCase?.()] || "#CCCCCC";
};